import React, { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, SubmitHandler } from "react-hook-form";
import { useLocation } from "react-router-dom";
import Logo from "../assets/images/normal_logo.png";
import { setPasswordSchema } from "../utils/setPasswordSchema";
import { ErrorMessage } from "@hookform/error-message";
import useResetNewPassword from "../hooks/useResetNewPassword";
import { Bounce, toast } from "react-toastify";
import "react-toastify/ReactToastify.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
const ResetPassword = (props) => {
  const location = useLocation();
  const [token, setToken] = useState("");
  const [resetUserName, setResetUserName] = useState("");
  const [passwordValue, setPasswordValue] = useState("");
  const [showPasswordRules, setShowPasswordRules] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const setNewPasswordMutation = useResetNewPassword();

  // Password validation rules
  const passwordRules = {
    minLength: passwordValue.length >= 8,
    hasUpperCase: /[A-Z]/.test(passwordValue),
    hasLowerCase: /[a-z]/.test(passwordValue),
    hasNumber: /\d/.test(passwordValue),
    hasSpecialChar: /[!@#$%^&*()\-_=+{};:,<.>]/.test(passwordValue),
  };

  // Check if all password rules are fulfilled
  const allRulesFulfilled =
    passwordRules.minLength &&
    passwordRules.hasUpperCase &&
    passwordRules.hasLowerCase &&
    passwordRules.hasNumber &&
    passwordRules.hasSpecialChar;

  // Decode JWT token to extract user information
  const decodeToken = (token) => {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  // Extract token from query parameters and decode it
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tokenParam = searchParams.get("token");
    if (tokenParam) {
      setToken(tokenParam);
      // Decode token to extract firstname
      const decodedToken = decodeToken(tokenParam);
      if (decodedToken) {
        // Try different possible field names for firstname
        const firstname =
          decodedToken.firstname ||
          decodedToken.firstName ||
          decodedToken.first_name ||
          decodedToken.name ||
          "User";
        setResetUserName(firstname);
      }
    }
  }, [location.search]);

  const {
    register: registerSetPassword,
    reset: resetPassword,
    handleSubmit: handlePassword,
    formState: { errors: errorsSetPassword },
  } = useForm({ resolver: yupResolver(setPasswordSchema) });

  const handleSetPassword = async (data) => {
    if (!token) {
      console.error("No token available");
      return;
    }

    setNewPasswordMutation.mutate(
      {
        token: token,
        password: data.password,
        confirmPassword: data.confirmPassword,
      },
      {
        onSuccess: (response) => {
          toast.success(
            <p className="text-center">
              Password has been chosen.
              <br />
              Please login with new password.
            </p>,
            {
              position: "top-right",
              autoClose: false,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              className: "bg_primary",
              theme: "colored",
              transition: Bounce,
            }
          );
          props.history.push("/user/login");
        },
        onError: (error) => {
          console.error("Password reset failed:", error);
        },
      }
    );
  };

  // Show error if no token is provided
  if (!token && location.search !== "") {
    return (
      <div className="main">
        <div className="col-lg-4">
          <div className="card border-0 shadow sm">
            <div className="card-body px-5 text-center">
              <h3 className="text-danger">Invalid Reset Link</h3>
              <p>The password reset link is invalid or has expired.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main">
      <div className="col-lg-4">
        <div className="card border-0 shadow sm">
          <div className="card-body px-5">
            <div className="text-center">
              <img
                src={Logo}
                className="img-fluid py-4"
                width={185}
                height={80}
                alt=""
              />
            </div>
            <div className="text-center mb-9">
              <h4 className="py-4 mb-0">
                Hello, <span>{resetUserName || "User"}</span>
              </h4>
              <h2 className="font-weight-bold">Reset password</h2>
            </div>
            <form className="mt-3" onSubmit={handlePassword(handleSetPassword)}>
              <div className="form-group position-relative">
                <div
                  className={`${
                    errorsSetPassword.password?.message && "input-error"
                  } d-flex align-items-center form-control`}
                  style={{ borderRadius: "8px", padding: "0 12px" }}
                >
                  <input
                    type={showPassword ? "text" : "password"}
                    className="border-0 outline-none flex-grow-1 text-dark"
                    style={{ background: "transparent", height: "38px" }}
                    placeholder="Choose Password"
                    {...registerSetPassword("password", {
                      onChange: (e) => {
                        setPasswordValue(e.target.value);
                        setShowPasswordRules(e.target.value.length > 0);
                      },
                    })}
                    disabled={setNewPasswordMutation.isLoading}
                    onFocus={() => passwordValue.length > 0 && setShowPasswordRules(true)}
                  />
                  <button
                    type="button"
                    className="btn p-0 border-0 cursor-pointer"
                    style={{ color: "#aaa" }}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                  </button>
                </div>
                
                {/* Password validation rules dropdown */}
                {showPasswordRules && !allRulesFulfilled && (
                  <div className="password-rules-dropdown mt-2 p-3 border rounded shadow-sm bg-white">
                    <label className="font-weight-bold mb-2" style={{ fontSize: '14px', color: '#353c51' }}>
                      Password must:
                    </label>
                    <ul className="list-unstyled mb-0">
                      <li className="d-flex align-items-center mb-2">
                        {passwordRules.minLength ? (
                          <span className="mr-2" style={{ fontSize: '16px', color: '#50a193' }}>✓</span>
                        ) : (
                          <span className="text-muted mr-2" style={{ fontSize: '16px' }}>○</span>
                        )}
                        <span style={{ fontSize: '13px', color: passwordRules.minLength ? '#50a193' : '#6c757d' }}>
                          Be at least 8 characters
                        </span>
                      </li>
                      <li className="d-flex align-items-center mb-2">
                        {passwordRules.hasUpperCase ? (
                          <span className="mr-2" style={{ fontSize: '16px', color: '#50a193' }}>✓</span>
                        ) : (
                          <span className="text-muted mr-2" style={{ fontSize: '16px' }}>○</span>
                        )}
                        <span style={{ fontSize: '13px', color: passwordRules.hasUpperCase ? '#50a193' : '#6c757d' }}>
                          Contain one uppercase letter
                        </span>
                      </li>
                      <li className="d-flex align-items-center mb-2">
                        {passwordRules.hasLowerCase ? (
                          <span className="mr-2" style={{ fontSize: '16px', color: '#50a193' }}>✓</span>
                        ) : (
                          <span className="text-muted mr-2" style={{ fontSize: '16px' }}>○</span>
                        )}
                        <span style={{ fontSize: '13px', color: passwordRules.hasLowerCase ? '#50a193' : '#6c757d' }}>
                          Contain one lowercase letter
                        </span>
                      </li>
                      <li className="d-flex align-items-center mb-2">
                        {passwordRules.hasNumber ? (
                          <span className="mr-2" style={{ fontSize: '16px', color: '#50a193' }}>✓</span>
                        ) : (
                          <span className="text-muted mr-2" style={{ fontSize: '16px' }}>○</span>
                        )}
                        <span style={{ fontSize: '13px', color: passwordRules.hasNumber ? '#50a193' : '#6c757d' }}>
                          Contain a number
                        </span>
                      </li>
                      <li className="d-flex align-items-center">
                        {passwordRules.hasSpecialChar ? (
                          <span className="mr-2" style={{ fontSize: '16px', color: '#50a193' }}>✓</span>
                        ) : (
                          <span className="text-muted mr-2" style={{ fontSize: '16px' }}>○</span>
                        )}
                        <span style={{ fontSize: '13px', color: passwordRules.hasSpecialChar ? '#50a193' : '#6c757d' }}>
                          Contain a special character (!@#$%^&*...)
                        </span>
                      </li>
                    </ul>
                  </div>
                )}
                
                <ErrorMessage
                  errors={errorsSetPassword}
                  name="password"
                  render={({ message }) => (
                    <p className="errorMessage small-text">{message}</p>
                  )}
                />
              </div>
              <div className="form-group py-1">
                <div
                  className={`${
                    errorsSetPassword.confirmPassword?.message && "input-error"
                  } d-flex align-items-center form-control`}
                  style={{ borderRadius: "8px", padding: "0 12px" }}
                >
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className="border-0 outline-none flex-grow-1 text-dark"
                    style={{ background: "transparent", height: "38px" }}
                    placeholder="Confirm Password"
                    {...registerSetPassword("confirmPassword")}
                    disabled={setNewPasswordMutation.isLoading}
                  />
                  <button
                    type="button"
                    className="btn p-0 border-0 cursor-pointer"
                    style={{ color: "#aaa" }}
                    aria-label={
                      showConfirmPassword ? "Hide confirm password" : "Show confirm password"
                    }
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                  >
                    {showConfirmPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                  </button>
                </div>
                <ErrorMessage
                  errors={errorsSetPassword}
                  name="confirmPassword"
                  render={({ message }) => (
                    <p className="errorMessage small-text">{message}</p>
                  )}
                />
              </div>

              {setNewPasswordMutation.isError && (
                <div className="text-danger small mb-3">
                  {setNewPasswordMutation.error?.message ||
                    "Failed to reset password. Please try again."}
                </div>
              )}

              {setNewPasswordMutation.isSuccess && (
                <div className="text-success small mb-3">
                  Password reset successfully! You can now login with your new
                  password.
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary btn-block my-4 py-2"
                disabled={setNewPasswordMutation.isLoading}
              >
                {setNewPasswordMutation.isLoading
                  ? "RESETTING PASSWORD..."
                  : "RESET Password"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
